import React from 'react'
import { useTranslation } from 'react-i18next'
import { BadgeCardProps } from '@pandiafi/components'

import type { CoreParkingSpot } from '../../../common/validations/contractParkingSpotsSchema'
import { fmtCurrency, normDate } from './parkingSpots.helpers'
import {
  spotTitle,
  spotPriceFor,
  isDisabledSpot,
  findActiveReservation,
  findFutureReservation,
  findPreviousReservation,
  formatDate,
} from './uiSelectors'

type Props = {
  spot: CoreParkingSpot
  addressLabel?: string
  status?: BadgeCardProps['status']
  canReserveDisabledSpots?: boolean
}

export const ParkingSpotTooltip: React.FC<Props> = ({
  spot,
  addressLabel,
  status,
  canReserveDisabledSpots = true,
}) => {
  const { t, i18n } = useTranslation('parking')
  const lang = i18n.language

  const title = spotTitle(spot)

  const price = spotPriceFor(spot, null)
  const priceLabel =
    typeof price === 'number'
      ? t('price.perMonth', { amount: fmtCurrency(price, lang) })
      : undefined

  const active = findActiveReservation(spot)
  const future = findFutureReservation(spot)
  const prev = findPreviousReservation(spot)
  const disabledReservationInfo =
    !canReserveDisabledSpots && isDisabledSpot(spot)

  return (
    <div className="flex flex-col gap-1 leading-normal">
      <div>{title}</div>
      <div>{addressLabel}</div>
      {priceLabel && <div>{priceLabel}</div>}
      {disabledReservationInfo && (
        <div>{t('view.disabledReservationInfo')}</div>
      )}

      {active && status !== 'disabled' && (
        <div>
          {t('tooltip.reservation.current.label')}:{' '}
          {formatDate(normDate(active.startDate)!, lang)}
          {active.endDate && normDate(active.endDate)
            ? ` - ${formatDate(normDate(active.endDate)!, lang)}`
            : ''}
        </div>
      )}

      {future && status !== 'disabled' && (
        <div>
          {t('tooltip.reservation.future.label')}:{' '}
          {formatDate(normDate(future.startDate)!, lang)}
          {future.endDate && normDate(future.endDate)
            ? ` - ${formatDate(normDate(future.endDate)!, lang)}`
            : ''}
        </div>
      )}

      {prev && status !== 'disabled' && (
        <div>
          {t('tooltip.reservation.previous.label')}:{' '}
          {formatDate(normDate(prev.startDate)!, lang)}
          {prev.endDate && normDate(prev.endDate)
            ? ` - ${formatDate(normDate(prev.endDate)!, lang)}`
            : ''}
        </div>
      )}
    </div>
  )
}
